import React, { useEffect, useMemo, useState } from 'react';
import { useUser } from '../contexts/UserContext';
import { usePwaInstall } from '../contexts/PwaInstallContext';
import ProfileImageUploadField from './ProfileImageUploadField';
import buildImageUrl from '../config/imageUrlHelper';
import { actualizarDatosUsuario, cambiarPassword, eliminarUsuario, obtenerEstadisticasUsuario } from '../services/userService';
import { AnimatePresence, motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';

const PROFILE_ICONS = {
  edit: 'https://cdn-icons-png.flaticon.com/512/5588/5588116.png',
  cancel: 'https://cdn-icons-png.flaticon.com/512/545/545676.png',
  save: 'https://cdn-icons-png.flaticon.com/512/376/376218.png',
  info: 'https://cdn-icons-png.flaticon.com/512/11803/11803487.png',
  status: 'https://cdn-icons-png.flaticon.com/512/2861/2861627.png',
  security: 'https://cdn-icons-png.flaticon.com/512/13084/13084816.png',
  trash: 'https://cdn-icons-png.flaticon.com/512/1214/1214428.png',
  download: 'https://cdn-icons-png.flaticon.com/512/2985/2985150.png'
};

const SidebarProfileIcon = ({ className = '' }) => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" className={className}>
    <path
      d="M12 12a4 4 0 1 0-4-4 4 4 0 0 0 4 4Zm0 2c-4.418 0-8 2.239-8 5v1h16v-1c0-2.761-3.582-5-8-5Z"
      fill="currentColor"
    />
  </svg>
);

const cardClass = 'rounded-2xl border border-slate-200/80 bg-white/90 p-5 shadow-sm backdrop-blur-sm dark:border-slate-700 dark:bg-slate-900/50';

const getIconClass = (size = 'w-4 h-4', tone = 'adaptive') => {
  const baseClass = `${size} object-contain`;
  return tone === 'button' ? `${baseClass} invert` : `${baseClass} dark:invert`;
};

const roleLabelMap = {
  admin: 'Administrador',
  moderador: 'Moderador',
  usuario: 'Usuario'
};

const roleBadgeClassMap = {
  admin: 'bg-rose-100 text-rose-700 dark:bg-rose-900/50 dark:text-rose-200',
  moderador: 'bg-amber-100 text-amber-700 dark:bg-amber-900/50 dark:text-amber-200',
  usuario: 'bg-sky-100 text-sky-700 dark:bg-sky-900/50 dark:text-sky-200'
};

const isValidEmail = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);

const UserProfile = () => {
  const { user, loading, error, checkAuth, logout } = useUser();
  const { canInstall, isInstalled, isIOS, promptInstall } = usePwaInstall();
  const navigate = useNavigate();
  const [installMessage, setInstallMessage] = useState('');

  const [isEditing, setIsEditing] = useState(false);
  const [editData, setEditData] = useState({});
  const [profileImageVersion, setProfileImageVersion] = useState(0);
  const [message, setMessage] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [mobileSectionOpen, setMobileSectionOpen] = useState('general');
  const [isDownloadSectionOpen, setIsDownloadSectionOpen] = useState(false);

  const [stats, setStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(false);

  const roleLabel = roleLabelMap[user?.rol] || user?.rol || 'Usuario';
  const roleBadgeClass = roleBadgeClassMap[user?.rol] || roleBadgeClassMap.usuario;
  const isStaffUser = user?.rol === 'admin' || user?.rol === 'moderador';

  const permissionEntries = useMemo(() => {
    if (!user?.permisos) return [];
    if (Array.isArray(user.permisos)) {
      return user.permisos.map((permiso) => [permiso, true]);
    }
    return Object.entries(user.permisos);
  }, [user?.permisos]);

  useEffect(() => {
    const fetchStats = async () => {
      if (!user?.id) {
        setStats(null);
        return;
      }

      setStatsLoading(true);
      try {
        const response = await obtenerEstadisticasUsuario(user.id);
        if (response?.success && response?.user?.stats) {
          setStats(response.user.stats);
        } else {
          setStats(null);
        }
      } catch (fetchError) {
        setStats(null);
      } finally {
        setStatsLoading(false);
      }
    };

    fetchStats();
  }, [user?.id]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="h-12 w-12 animate-spin rounded-full border-b-2 border-t-2 border-blue-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded border border-red-400 bg-red-100 px-4 py-3 text-red-700 dark:border-red-700 dark:bg-red-900 dark:text-red-200" role="alert">
        <strong className="font-bold">Error: </strong>
        <span className="block sm:inline">{error}</span>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="rounded border border-yellow-400 bg-yellow-100 px-4 py-3 text-yellow-700 dark:border-yellow-700 dark:bg-yellow-900 dark:text-yellow-200" role="alert">
        <strong className="font-bold">No autenticado: </strong>
        <span className="block sm:inline">Por favor, inicia sesion para ver tu perfil.</span>
      </div>
    );
  }

  const enterEditMode = () => {
    setEditData({
      username: user.username || '',
      email: user.email || '',
      foto_perfil: user.foto_perfil || '',
      currentPassword: '',
      newPassword: '',
      confirmPassword: ''
    });
    setIsEditing(true);
  };

  const exitEditMode = (clearMessage = true) => {
    setEditData({});
    if (clearMessage) {
      setMessage('');
    }
    setIsEditing(false);
  };

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setEditData((prev) => ({
      ...prev,
      [name]: value
    }));
  };

  const handleProfileImageUploaded = async (path, successMessage, version) => {
    setEditData((prev) => ({
      ...prev,
      foto_perfil: path
    }));

    if (version) {
      setProfileImageVersion(version);
    }

    try {
      const imageResponse = await actualizarDatosUsuario({
        foto_perfil: path
      });

      if (imageResponse?.success) {
        setMessage(successMessage || 'Foto de perfil actualizada correctamente.');
      } else {
        setMessage(imageResponse?.message || 'La foto se aplico en la vista previa, pero no se pudo guardar.');
      }
    } catch (imageSaveError) {
      setMessage('La foto se aplico en la vista previa, pero no se pudo guardar.');
    }

    await checkAuth();
  };

  const getProfileImageUrl = (path) => {
    const resolvedUrl = buildImageUrl(path);
    if (!resolvedUrl || !profileImageVersion) {
      return resolvedUrl;
    }

    const separator = resolvedUrl.includes('?') ? '&' : '?';
    return `${resolvedUrl}${separator}v=${profileImageVersion}`;
  };

  const validateEditData = () => {
    const username = (editData.username || '').trim();
    const email = (editData.email || '').trim();

    if (!username) {
      return 'El nombre de usuario no puede estar vacio.';
    }

    if (!email || !isValidEmail(email)) {
      return 'El correo electronico no tiene un formato valido.';
    }

    const wantsPasswordChange = Boolean(
      editData.currentPassword || editData.newPassword || editData.confirmPassword
    );

    if (wantsPasswordChange) {
      if (!editData.currentPassword) {
        return 'Debes introducir la contraseña actual para cambiarla.';
      }

      if (!editData.newPassword) {
        return 'Debes introducir una nueva contraseña.';
      }

      if (editData.newPassword.length < 6) {
        return 'La nueva contraseña debe tener al menos 6 caracteres.';
      }

      if (editData.newPassword !== editData.confirmPassword) {
        return 'La nueva contraseña y su confirmación no coinciden.';
      }
    }

    return '';
  };

  const handleSave = async () => {
    setMessage('');

    const validationMessage = validateEditData();
    if (validationMessage) {
      setMessage(validationMessage);
      return;
    }

    setIsSaving(true);

    try {
      const updatePayload = {
        username: (editData.username || '').trim(),
        email: (editData.email || '').trim(),
        foto_perfil: editData.foto_perfil || ''
      };

      const updateResponse = await actualizarDatosUsuario(updatePayload);
      if (!updateResponse?.success) {
        setMessage(updateResponse?.message || 'Error al actualizar el perfil.');
        return;
      }

      const wantsPasswordChange = Boolean(
        editData.currentPassword || editData.newPassword || editData.confirmPassword
      );

      if (wantsPasswordChange) {
        const passwordResponse = await cambiarPassword(editData.currentPassword, editData.newPassword);
        if (!passwordResponse?.success) {
          setMessage(passwordResponse?.message || 'Perfil actualizado, pero no se pudo cambiar la contraseña.');
          await checkAuth();
          return;
        }
      }

      setMessage('Perfil actualizado correctamente.');
      exitEditMode(false);
      await checkAuth();
    } catch (saveError) {
      setMessage('Error de conexion al actualizar el perfil.');
    } finally {
      setIsSaving(false);
    }
  };

  const openDeleteModal = () => {
    setShowDeleteModal(true);
  };

  const closeDeleteModal = () => {
    if (isDeleting) return;
    setShowDeleteModal(false);
  };

  const handleDeleteUser = async () => {
    if (!user?.id) {
      setMessage('No se pudo determinar el usuario a eliminar.');
      setShowDeleteModal(false);
      return;
    }

    setIsDeleting(true);

    try {
      const response = await eliminarUsuario(user.id);

      if (!response?.success) {
        setMessage(response?.message || 'No se pudo eliminar el usuario.');
        setIsDeleting(false);
        return;
      }

      setShowDeleteModal(false);
      await logout();
      navigate('/');
    } catch (deleteError) {
      setMessage('Error de red al eliminar el usuario.');
      setIsDeleting(false);
    }
  };

  const renderIconText = (iconUrl, text, iconAlt, tone = 'button') => (
    <span className="inline-flex items-center gap-2">
      <img src={iconUrl} alt={iconAlt} className={getIconClass('w-4 h-4', tone)} loading="lazy" />
      <span>{text}</span>
    </span>
  );

  const renderInfoRow = (label, value, badgeClass = '') => (
    <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-1 sm:gap-3 border-b border-slate-100 py-2 dark:border-slate-800 last:border-b-0">
      <span className="font-medium text-slate-600 dark:text-slate-300">{label}</span>
      {badgeClass ? (
        <span className={`rounded-md px-2 py-0.5 text-xs font-semibold self-start sm:self-auto ${badgeClass}`}>{value}</span>
      ) : (
        <span className="text-left sm:text-right text-slate-900 dark:text-white break-words">{value}</span>
      )}
    </div>
  );

  const toggleMobileSection = (sectionKey) => {
    setMobileSectionOpen((prev) => (prev === sectionKey ? null : sectionKey));
  };

  const handleInstallClick = async () => {
    const choice = await promptInstall();
    if (choice?.outcome === 'dismissed') {
      setInstallMessage('Instalación cancelada. Puedes intentarlo de nuevo cuando quieras.');
    }
  };

  const toggleDownloadSection = () => {
    setIsDownloadSectionOpen((prev) => !prev);
  };

  const renderInstallCard = () => (
    <div className={cardClass}>
      <button
        type="button"
        onClick={toggleDownloadSection}
        aria-expanded={isDownloadSectionOpen}
        className="flex w-full items-center justify-between gap-2 text-left"
      >
        <span className="inline-flex items-center gap-2 text-lg font-semibold text-slate-800 dark:text-slate-100">
          <img src={PROFILE_ICONS.download} alt="Icono descarga" className={getIconClass('h-5 w-5', 'adaptive')} loading="lazy" />
          <span>{isInstalled ? 'App instalada' : 'Descargar app'}</span>
        </span>
        <svg
          width="18"
          height="18"
          viewBox="0 0 24 24"
          fill="none"
          className={`shrink-0 text-slate-500 transition-transform duration-300 dark:text-slate-400 ${isDownloadSectionOpen ? 'rotate-180' : ''}`}
        >
          <path d="M6 9l6 6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>

      <AnimatePresence initial={false}>
        {isDownloadSectionOpen && (
          <motion.div
            key="download-content"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.28, ease: 'easeInOut' }}
            className="overflow-hidden"
          >
            <div className="mt-3">
              {isInstalled ? (
                <p className="text-sm text-slate-600 dark:text-slate-300">
                  Ya tienes Diegoncurso instalado en este dispositivo. Búscalo en tu pantalla de inicio.
                </p>
              ) : (
                <>
                  <p className="mb-3 text-sm text-slate-600 dark:text-slate-300">
                    Instala Diegoncurso en tu móvil para acceder más rápido, con su propio icono en la pantalla de inicio.
                  </p>

                  {canInstall && (
                    <button
                      type="button"
                      onClick={handleInstallClick}
                      className="w-full sm:w-auto rounded-lg bg-blue-600 px-4 py-2 font-medium text-white transition-colors duration-300 hover:bg-blue-700 dark:bg-blue-700 dark:hover:bg-blue-800"
                    >
                      {renderIconText(PROFILE_ICONS.download, 'Instalar aplicación', 'Icono instalar')}
                    </button>
                  )}

                  {!canInstall && isIOS && (
                    <p className="text-sm text-slate-600 dark:text-slate-300">
                      En iPhone o iPad: toca el botón <strong>Compartir</strong> de Safari y elige <strong>«Añadir a pantalla de inicio»</strong>.
                    </p>
                  )}

                  {!canInstall && !isIOS && (
                    <p className="text-sm text-slate-500 dark:text-slate-400">
                      Abre esta página con Chrome o Edge en tu móvil para poder instalarla.
                    </p>
                  )}

                  {installMessage && (
                    <p className="mt-3 text-sm text-amber-600 dark:text-amber-300">{installMessage}</p>
                  )}
                </>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );

  const renderGeneralCard = () => (
    <div className={cardClass}>
      <h4 className="mb-4 inline-flex items-center gap-2 text-lg font-semibold text-slate-800 dark:text-slate-100">
        <img src={PROFILE_ICONS.info} alt="Icono informacion general" className={getIconClass('h-5 w-5', 'adaptive')} loading="lazy" />
        <span>Informacion general</span>
      </h4>

      {renderInfoRow('Nombre de usuario', user.username || 'No disponible')}
      {renderInfoRow('Correo electronico', user.email || 'No disponible')}
      {renderInfoRow('Fecha de registro', user.fecha_registro ? new Date(user.fecha_registro).toLocaleString() : 'No disponible')}
      {isStaffUser && renderInfoRow('Rol', roleLabel, roleBadgeClass)}
      {isStaffUser && renderInfoRow('ID de cuenta', user.id || 'No disponible')}
      {isStaffUser && renderInfoRow('Permisos disponibles', String(permissionEntries.length || 0))}
    </div>
  );

  const renderStatusCard = () => (
    <div className={cardClass}>
      <h4 className="mb-4 inline-flex items-center gap-2 text-lg font-semibold text-slate-800 dark:text-slate-100">
        <img src={PROFILE_ICONS.status} alt="Icono estado de cuenta" className={getIconClass('h-5 w-5', 'adaptive')} loading="lazy" />
        <span>Estado de la cuenta</span>
      </h4>

      {renderInfoRow('Cuenta verificada', isVerified === undefined || isVerified === null ? 'No disponible' : (isVerified ? 'Verificada' : 'No verificada'))}
      {renderInfoRow('Estado de suscripcion', subscriptionStatus)}
      {renderInfoRow('Ultimo acceso', user.ultimo_acceso ? new Date(user.ultimo_acceso).toLocaleString() : 'No disponible')}
      {renderInfoRow('Estado de sesion', user.activo ? 'Activo' : 'Inactivo')}

      <div className="mt-4 rounded-xl border border-slate-200 p-3 dark:border-slate-700">
        <h5 className="mb-2 inline-flex items-center gap-2 text-sm font-semibold text-slate-700 dark:text-slate-200">
          <img src={PROFILE_ICONS.security} alt="Icono estadisticas" className={getIconClass('h-4 w-4', 'adaptive')} loading="lazy" />
          <span>Estadisticas relevantes</span>
        </h5>

        {statsLoading && <p className="text-sm text-slate-500 dark:text-slate-300">Cargando estadisticas...</p>}

        {!statsLoading && stats && (
          <div className="space-y-1 text-sm text-slate-600 dark:text-slate-300">
            <p>Total preguntas enviadas: {stats.total_preguntas_enviadas ?? 0}</p>
            <p>Preguntas aprobadas: {stats.preguntas_aprobadas ?? 0}</p>
            <p>Preguntas pendientes: {stats.preguntas_pendientes ?? 0}</p>
            <p>Preguntas rechazadas: {stats.preguntas_rechazadas ?? 0}</p>
          </div>
        )}

        {!statsLoading && !stats && (
          <p className="text-sm text-slate-500 dark:text-slate-300">No hay estadisticas disponibles para este perfil.</p>
        )}
      </div>
    </div>
  );

  const profileImagePath = isEditing ? (editData.foto_perfil || '') : (user.foto_perfil || '');
  const profileImageUrl = getProfileImageUrl(profileImagePath || user.foto_perfil || '');
  const isVerified = user?.verificado;
  const subscriptionStatus = user?.suscripcion_estado || user?.subscription_status || 'No disponible';

  return (
    <div className={`mx-auto flex max-h-[calc(100dvh-4rem)] max-w-6xl flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-xl transition-colors duration-300 dark:border-slate-700 dark:bg-slate-900 ${isDownloadSectionOpen ? '' : 'md:max-h-none'}`}>
      <div className="relative shrink-0 border-b border-slate-200 bg-gradient-to-r from-sky-50 via-white to-indigo-50 px-6 py-6 dark:border-slate-700 dark:from-slate-800 dark:via-slate-900 dark:to-slate-800">
        <div className="absolute -right-8 -top-8 h-32 w-32 rounded-full bg-sky-200/40 blur-2xl dark:bg-sky-500/10"></div>
        <div className="absolute -bottom-8 -left-6 h-28 w-28 rounded-full bg-indigo-200/40 blur-2xl dark:bg-indigo-500/10"></div>

        <div className="relative flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
            <div className="flex h-20 w-20 shrink-0 items-center justify-center overflow-hidden rounded-full border-4 border-white bg-slate-200 shadow-md dark:border-slate-700 dark:bg-slate-700">
              {profileImageUrl ? (
                <img
                  src={profileImageUrl}
                  alt="Foto de perfil"
                  className="h-full w-full object-cover"
                />
              ) : (
                <SidebarProfileIcon className="h-9 w-9 text-slate-500 dark:text-slate-300" />
              )}
            </div>

            <div>
              <h3 className="flex items-center gap-2 min-w-0 text-xl sm:text-2xl font-bold text-slate-900 dark:text-white break-words">
                <SidebarProfileIcon className="h-7 w-7 shrink-0 text-slate-900 dark:text-white" />
                <span className="min-w-0 break-words">Hola, {user.username}</span>
              </h3>
              <p className="mt-1 text-sm text-slate-600 dark:text-slate-300">Resumen de cuenta, estado y seguridad.</p>
            </div>
          </div>

          <button
            onClick={isEditing ? () => exitEditMode(true) : enterEditMode}
            className={`rounded-lg px-4 py-2 font-medium transition-colors duration-300 ${
              isEditing
                ? 'bg-slate-500 text-white hover:bg-slate-600'
                : 'bg-blue-600 text-white hover:bg-blue-700 dark:bg-blue-700 dark:hover:bg-blue-800'
            }`}
          >
            {isEditing
              ? renderIconText(PROFILE_ICONS.cancel, 'Cancelar', 'Icono cancelar')
              : renderIconText(PROFILE_ICONS.edit, 'Editar perfil', 'Icono editar perfil')}
          </button>
        </div>
      </div>

      <div className={`flex-1 overflow-y-auto p-6 ${isDownloadSectionOpen ? '' : 'md:overflow-visible'}`}>
        {message && (
          <div
            className={`mb-5 rounded-lg border p-3 ${
              message.toLowerCase().includes('correctamente')
                ? 'border-green-400 bg-green-100 text-green-700 dark:border-green-700 dark:bg-green-900 dark:text-green-200'
                : 'border-red-400 bg-red-100 text-red-700 dark:border-red-700 dark:bg-red-900 dark:text-red-200'
            }`}
          >
            {message}
          </div>
        )}

        {!isEditing && (
          <div className="mb-5 flex flex-wrap gap-2">
            {isStaffUser && (
              <span className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold ${roleBadgeClass}`}>
                Rol: {roleLabel}
              </span>
            )}
            <span className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold ${user.activo ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/50 dark:text-emerald-200' : 'bg-slate-200 text-slate-700 dark:bg-slate-700 dark:text-slate-200'}`}>
              Estado: {user.activo ? 'Activo' : 'Inactivo'}
            </span>
            <span className="inline-flex items-center rounded-full bg-indigo-100 px-3 py-1 text-xs font-semibold text-indigo-700 dark:bg-indigo-900/50 dark:text-indigo-200">
              Ultimo acceso: {user.ultimo_acceso ? new Date(user.ultimo_acceso).toLocaleString() : 'No disponible'}
            </span>
          </div>
        )}

        {isEditing ? (
          <div className="grid grid-cols-1 gap-6 xl:grid-cols-5">
            <div className={`${cardClass} xl:col-span-3`}>
              <h4 className="mb-4 inline-flex items-center gap-2 text-lg font-semibold text-slate-800 dark:text-slate-100">
                <img src={PROFILE_ICONS.info} alt="Icono informacion" className={getIconClass('h-5 w-5', 'adaptive')} loading="lazy" />
                <span>Editar perfil</span>
              </h4>

              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div>
                  <label className="mb-2 block text-gray-700 dark:text-gray-300">Nombre de usuario</label>
                  <input
                    type="text"
                    name="username"
                    value={editData.username || ''}
                    onChange={handleInputChange}
                    className="w-full rounded-lg border border-gray-300 p-3 transition-colors duration-300 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                  />
                </div>

                <div>
                  <label className="mb-2 block text-gray-700 dark:text-gray-300">Correo electronico</label>
                  <input
                    type="email"
                    name="email"
                    value={editData.email || ''}
                    onChange={handleInputChange}
                    className="w-full rounded-lg border border-gray-300 p-3 transition-colors duration-300 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                  />
                </div>

                <div>
                  <label className="mb-2 block text-gray-700 dark:text-gray-300">Contraseña actual</label>
                  <input
                    type="password"
                    name="currentPassword"
                    value={editData.currentPassword || ''}
                    onChange={handleInputChange}
                    placeholder="Requerida para cambiar contraseña"
                    className="w-full rounded-lg border border-gray-300 p-3 transition-colors duration-300 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                  />
                </div>

                <div>
                  <label className="mb-2 block text-gray-700 dark:text-gray-300">Nueva contraseña</label>
                  <input
                    type="password"
                    name="newPassword"
                    value={editData.newPassword || ''}
                    onChange={handleInputChange}
                    placeholder="Minimo 6 caracteres"
                    className="w-full rounded-lg border border-gray-300 p-3 transition-colors duration-300 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="mb-2 block text-gray-700 dark:text-gray-300">Confirmar nueva contraseña</label>
                  <input
                    type="password"
                    name="confirmPassword"
                    value={editData.confirmPassword || ''}
                    onChange={handleInputChange}
                    placeholder="Repite la nueva contraseña"
                    className="w-full rounded-lg border border-gray-300 p-3 transition-colors duration-300 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                  />
                </div>
              </div>

              <div className="mt-5 flex flex-col sm:flex-row sm:justify-end gap-3">
                <button
                  onClick={openDeleteModal}
                  disabled={isSaving || isDeleting}
                  className="w-full sm:w-auto rounded-lg bg-red-600 px-4 py-2 text-white transition-colors duration-300 hover:bg-red-700 disabled:opacity-50"
                >
                  {renderIconText(PROFILE_ICONS.trash, 'Eliminar usuario', 'Icono eliminar usuario')}
                </button>
                <button
                  onClick={() => exitEditMode(true)}
                  disabled={isSaving || isDeleting}
                  className="w-full sm:w-auto rounded-lg bg-slate-500 px-4 py-2 text-white transition-colors duration-300 hover:bg-slate-600 disabled:opacity-50"
                >
                  {renderIconText(PROFILE_ICONS.cancel, 'Cancelar', 'Icono cancelar')}
                </button>
                <button
                  onClick={handleSave}
                  disabled={isSaving || isDeleting}
                  className="w-full sm:w-auto rounded-lg bg-green-600 px-4 py-2 text-white transition-colors duration-300 hover:bg-green-700 disabled:opacity-50"
                >
                  {isSaving
                    ? renderIconText(PROFILE_ICONS.save, 'Guardando...', 'Icono guardar')
                    : renderIconText(PROFILE_ICONS.save, 'Guardar cambios', 'Icono guardar')}
                </button>
              </div>
            </div>

            <div className={`${cardClass} xl:col-span-2`}>
              <h4 className="mb-4 inline-flex items-center gap-2 text-lg font-semibold text-slate-800 dark:text-slate-100">
                <img src={PROFILE_ICONS.status} alt="Icono foto" className={getIconClass('h-5 w-5', 'adaptive')} loading="lazy" />
                <span>Foto de perfil</span>
              </h4>

              <ProfileImageUploadField
                value={editData.foto_perfil || ''}
                onUploaded={handleProfileImageUploaded}
                disabled={isSaving}
              />
            </div>
          </div>
        ) : (
          <>
            <div className="mb-5">
              {renderInstallCard()}
            </div>

            <div className="md:hidden">
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => toggleMobileSection('general')}
                  className={`flex-1 inline-flex items-center justify-center gap-2 rounded-xl border px-3 py-2.5 text-sm font-semibold transition-colors ${
                    mobileSectionOpen === 'general'
                      ? 'border-blue-500 bg-blue-50 text-blue-700 dark:border-blue-400 dark:bg-blue-900/30 dark:text-blue-200'
                      : 'border-slate-300 bg-white text-slate-700 dark:border-slate-700 dark:bg-slate-900/40 dark:text-slate-200'
                  }`}
                >
                  <img src={PROFILE_ICONS.info} alt="Icono informacion" className={getIconClass('h-4 w-4', 'adaptive')} loading="lazy" />
                  <span>Info. general</span>
                </button>

                <button
                  type="button"
                  onClick={() => toggleMobileSection('estado')}
                  className={`flex-1 inline-flex items-center justify-center gap-2 rounded-xl border px-3 py-2.5 text-sm font-semibold transition-colors ${
                    mobileSectionOpen === 'estado'
                      ? 'border-blue-500 bg-blue-50 text-blue-700 dark:border-blue-400 dark:bg-blue-900/30 dark:text-blue-200'
                      : 'border-slate-300 bg-white text-slate-700 dark:border-slate-700 dark:bg-slate-900/40 dark:text-slate-200'
                  }`}
                >
                  <img src={PROFILE_ICONS.status} alt="Icono estado" className={getIconClass('h-4 w-4', 'adaptive')} loading="lazy" />
                  <span>Estado</span>
                </button>
              </div>

              <AnimatePresence mode="wait" initial={false}>
                {mobileSectionOpen && (
                  <motion.div
                    key={mobileSectionOpen}
                    initial={{ height: 0, opacity: 0, y: -8 }}
                    animate={{ height: 'auto', opacity: 1, y: 0 }}
                    exit={{ height: 0, opacity: 0, y: -8 }}
                    transition={{ duration: 0.28, ease: 'easeInOut' }}
                    className="overflow-hidden mt-4"
                  >
                    {mobileSectionOpen === 'general' ? renderGeneralCard() : renderStatusCard()}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            <div className="hidden md:grid grid-cols-1 gap-6 lg:grid-cols-2">
              {renderGeneralCard()}
              {renderStatusCard()}
            </div>
          </>
        )}
      </div>

      <AnimatePresence>
        {showDeleteModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 p-4"
            role="dialog"
            aria-modal="true"
            aria-label="Confirmar eliminacion de usuario"
          >
            <motion.div
              initial={{ opacity: 0, y: 20, scale: 0.98 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 12, scale: 0.98 }}
              transition={{ duration: 0.2 }}
              className="w-full max-w-md rounded-2xl border border-slate-200 bg-white p-6 shadow-2xl dark:border-slate-700 dark:bg-slate-900"
            >
              <h4 className="text-lg font-bold text-slate-900 dark:text-white">Eliminar usuario</h4>
              <p className="mt-2 text-sm text-slate-600 dark:text-slate-300">
                Vas a eliminar tu usuario de forma permanente. Esta accion no se puede deshacer.
              </p>

              <div className="mt-5 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
                <button
                  type="button"
                  onClick={closeDeleteModal}
                  disabled={isDeleting}
                  className="rounded-lg bg-slate-200 px-4 py-2 text-slate-800 transition-colors hover:bg-slate-300 disabled:opacity-50 dark:bg-slate-700 dark:text-slate-100 dark:hover:bg-slate-600"
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  onClick={handleDeleteUser}
                  disabled={isDeleting}
                  className="rounded-lg bg-red-600 px-4 py-2 text-white transition-colors hover:bg-red-700 disabled:opacity-50"
                >
                  {isDeleting ? 'Eliminando...' : 'Confirmar eliminacion'}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default UserProfile;

